import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, OnChanges, Optional } from '@angular/core';

import { ParticipantOption, ParticipantPriceType } from '@cds/betting-offer';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { exhaustiveCheckGuard } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { V2WinPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { PriceType } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import {
    BETSLIP_LINE_SWITCHER_INJECTION_TOKEN,
    ILineSwitcherPickHandlerService,
} from 'packages/sports/common/betslip/integration/services/line-switcher-pick-handler.service';

import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import { HorsesFixturePricingHelper } from '../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickSourceProvider } from './pick-source.provider';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-fixture-pick',
    templateUrl: 'pick.component.html',
    standalone: true,
    imports: [NgClass, OptionIndicatorDirective, AutomationModule, AsyncPipe],
})
export class FixturePickComponent extends PickComponent implements OnChanges {
    @Input() participant: WinGameParticipant;
    @Input() fixture: WinGameFixture;
    @Input() option: ParticipantOption;
    @Input() enablePriceTypeCheck = false;
    @Input() parentLinkedEventId?: string;
    @Input() trackingOptions?: { [key: string]: string };

    override pickId: V2WinPickId;

    constructor(
        geolocationCheckService: GeolocationCheckService,
        private horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private pickSourceProvider: PickSourceProvider,
        betslipIntegration: BetslipIntegrationService,
        private store: Store,
        changeDetection: ChangeDetectorRef,
        @Inject(BETSLIP_LINE_SWITCHER_INJECTION_TOKEN) @Optional() lineSwitcherPickHandlerService: ILineSwitcherPickHandlerService | null,
    ) {
        super(geolocationCheckService, betslipIntegration, changeDetection, lineSwitcherPickHandlerService);
    }

    ngOnChanges(changes: ISimpleChanges<FixturePickComponent>): void {
        this.state.locked = this.horsesFixturePricingHelper.isPriceLocked(this.fixture, this.participant, this.option);
        this.setPickId(this.createPickId(), this.enablePriceTypeCheck ? this.getPriceType(this.option.priceType) : undefined);
    }

    private createPickId(): V2WinPickId {
        return new V2WinPickId(this.fixture.id, this.option.marketId, this.participant.id);
    }

    get price(): NativePrice {
        return NativePrice.fromNativePrice(this.option.price);
    }

    get priceId(): number | undefined {
        return this.option.price.id;
    }

    protected addPick(): void {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            undefined,
            undefined,
            undefined,
            undefined,
            this.trackingOptions,
        );
        this.pickId.parentLinkedEventId = this.parentLinkedEventId;

        this.store.dispatch(
            ExternalBetslipActions.addPick({
                pickId: this.pickId,
                tracking: pickSource,
                priceId: this.option.price.id,
                parentLinkedEventId: this.parentLinkedEventId,
            }),
        );
    }

    protected removePick(): void {
        this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
    }

    private getPriceType(priceType: ParticipantPriceType): PriceType {
        switch (priceType) {
            case ParticipantPriceType.Fixed:
                return PriceType.Fixed;
            case ParticipantPriceType.NoPrice:
                return PriceType.NoPrice;
            case ParticipantPriceType.StartingPrice:
                return PriceType.StartingPrice;
            default:
                return exhaustiveCheckGuard(priceType);
        }
    }
}
